/**
 * Per-flow-type default copy for the thank-you page.
 *
 * Used as placeholders in the builder (ThankYouStep), as fallback display
 * in previews (FlowPreview, ThankYou page), and as the seed value when
 * creating a new flow (useFlowForm).
 */

type FlowType = string | undefined | null;

const norm = (t: FlowType): string =>
  (t ?? "").toString().trim().toLowerCase().replace(/[-_]/g, "");

export function getDefaultThankYouMessage(flowType: FlowType): string {
  switch (norm(flowType)) {
    case "signup":
    case "waitlist":
      return "Thanks! You're on the list. We'll notify you as soon as we launch.";
    case "newsletter":
      return "Thanks for subscribing! You'll hear from us soon.";
    case "referral":
      return "Thanks for sharing! We'll credit your rewards once your referrals sign up.";
    case "preorder":
      return "Thanks for pre-ordering! We'll keep you posted as we get closer to launch.";
    case "pricingtable":
      return "Thanks for checking out our pricing — your interest has been recorded.";
    case "discount":
    case "discounttest":
      return "Thanks! Your interest at this price has been recorded.";
    case "buybutton":
    case "payment":
    case "subscription":
    default:
      return "Thanks for your interest! Your response has been recorded.";
  }
}

/**
 * Returns the default disclaimer note shown below the thank-you message.
 * Returns null for flow types where a disclaimer shouldn't show by default.
 */
export function getDefaultThankYouNote(flowType: FlowType): string | null {
  const t = norm(flowType);
  // Disclaimer shows by default only for flows that simulate a transaction
  const wantsDisclaimer =
    t === "payment" ||
    t === "subscription" ||
    t === "pricingtable" ||
    t === "preorder" ||
    t === "discount" ||
    t === "discounttest" ||
    t === "buybutton";

  if (!wantsDisclaimer) return null;
  return "This was a simulated flow. No information was saved.";
}
